import React from 'react';
import { Icon } from '@iconify/react';
import { useSimulation } from '../../contexts/SimulationContext';
import './AdminDashboard.css';

const LiveCommandCenter = () => {
  const { project, pipeline, alerts, devices, candidates } = useSimulation();

  // Compute real stats from candidates array
  const totalCandidates = candidates.length;
  const rejected = candidates.filter(c => c.status === 'REJECTED').length;
  const completed = candidates.filter(c => c.status === 'COMPLETED').length;
  const active = totalCandidates - rejected - completed;

  let totalTests = 0, passedTests = 0;
  candidates.forEach(c => {
    (c.test_results || []).forEach(tr => {
      totalTests++;
      if (tr.result === 'PASS') passedTests++;
    });
  });
  const passRate = totalTests > 0 ? Math.round((passedTests / totalTests) * 100) : 0;
  const failRate = totalTests > 0 ? 100 - passRate : 0;

  const stages = [
    { id: 'entry', label: 'Entry Gate', icon: 'ion:enter-outline', color: '#3b82f6' },
    { id: 'batch', label: 'Batching', icon: 'ion:people-outline', color: '#8b5cf6' },
    { id: 'run', label: 'Track Run', icon: 'ion:walk-outline', color: '#f59e0b' },
    { id: 'stage', label: 'Physical Tests', icon: 'ion:clipboard-outline', color: '#10b981' },
    { id: 'medical', label: 'Medical Check', icon: 'ion:medkit-outline', color: '#ef4444' }
  ];

  return (
    <div className="main-view">
      {/* Top Metrics Header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '32px' }}>
        <MetricCard title="Total Registered" value={totalCandidates} icon="ion:people-circle-outline" trend={null} />
        <MetricCard title="Active in Pipeline" value={active} icon="ion:sync-outline" color="#3b82f6" />
        <MetricCard title="Eliminated" value={rejected} icon="ion:close-circle-outline" color="#ef4444" />
        <MetricCard title="Completed" value={completed} icon="ion:checkmark-circle-outline" color="#10b981" />
      </div>

      {/* Live Flow Visualizer */}
      <div className="glass-card" style={{ marginBottom: '32px', padding: '40px 24px' }}>
        <h3 style={{ marginBottom: '32px', fontSize: '18px', fontWeight: 800 }}>LIVE FLOW PIPELINE</h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
          {stages.map((stage, index) => {
            const isPhysical = stage.id === 'stage';
            const hasSubStages = isPhysical && pipeline.subStages && Object.keys(pipeline.subStages).length > 0;

            return (
              <React.Fragment key={stage.id}>
                <div 
                  className="pipeline-stage-container"
                  style={{ textAlign: 'center', zIndex: 1, flex: 1, position: 'relative' }}
                >
                  <div className="stage-icon-wrapper" style={{ 
                    width: '64px', 
                    height: '64px', 
                    background: stage.color, 
                    borderRadius: '16px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    margin: '0 auto 12px',
                    color: 'white',
                    boxShadow: `0 8px 16px -4px ${stage.color}44`,
                    cursor: isPhysical ? 'pointer' : 'default',
                    position: 'relative'
                  }}>
                    <Icon icon={stage.icon} width="32" height="32" />
                    
                    {/* Sub-stage Hover Reveal (Connected Flow Design) */}
                    {hasSubStages && (
                      <div className="sub-stage-tooltip glass-card" style={{
                        position: 'absolute',
                        bottom: '120%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '260px',
                        zIndex: 100,
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        border: `2px solid ${stage.color}`,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                        textAlign: 'left'
                      }}>
                        <div className="sub-stage-tooltip-content hide-scrollbar">
                          <div style={{ color: stage.color, fontSize: '11px', fontWeight: 900, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '2px', borderBottom: `1px solid ${stage.color}22`, paddingBottom: '8px' }}>
                            Physical Workflow Distribution
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {project.stages.map((s, idx) => (
                              <React.Fragment key={s.name}>
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '8px 12px',
                                  background: pipeline.subStages[s.name] > 0 ? `${stage.color}11` : 'transparent',
                                  borderRadius: '8px',
                                  border: pipeline.subStages[s.name] > 0 ? `1px solid ${stage.color}22` : '1px solid transparent'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ 
                                      width: '8px', 
                                      height: '8px', 
                                      borderRadius: '50%', 
                                      background: pipeline.subStages[s.name] > 0 ? stage.color : '#cbd5e1' 
                                    }}></div>
                                    <span style={{ fontSize: '12px', fontWeight: 700, color: pipeline.subStages[s.name] > 0 ? 'var(--text-main)' : 'var(--text-muted)' }}>{s.name}</span>
                                  </div>
                                  <span style={{ fontSize: '14px', fontWeight: 800, color: stage.color }}>{pipeline.subStages[s.name] || 0}</span>
                                </div>
                                {idx < project.stages.length - 1 && (
                                  <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0' }}>
                                    <Icon icon="ion:arrow-down-outline" width="12" color={stage.color} style={{ opacity: 0.4 }} />
                                  </div>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ fontWeight: 800, fontSize: '24px', marginBottom: '4px' }}>{pipeline[stage.id]}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{stage.label}</div>
                </div>
                {index < stages.length - 1 && (
                  <div style={{ flex: 0.5, height: '2px', background: 'var(--border-light)', marginTop: '-40px', position: 'relative' }}>
                    <div className="pipeline-progress" style={{ width: '100%', height: '100%', background: 'var(--primary)', opacity: 0.2 }}></div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '32px', marginBottom: '32px' }}>
        {/* Active Operations Panel */}
        <div className="glass-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 800 }}>ACTIVE OPERATIONS</h3>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>Live Track Status</div>
          </div>
          <div className="hide-scrollbar" style={{ maxHeight: '280px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-light)' }}>
                  <th style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Pipeline Stage</th>
                  <th style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Status</th>
                  <th style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Load</th>
                </tr>
              </thead>
              <tbody>
                <OpRow name="Entry Gate" count={pipeline.entry} />
                <OpRow name="Batching Zone" count={pipeline.batch} />
                <OpRow name="Track Run" count={pipeline.run} />
                <OpRow name="Physical Tests" count={pipeline.stage} />
                <OpRow name="Medical Check" count={pipeline.medical} />
                <OpRow name="Completed" count={completed} type="completed" />
                <OpRow name="Eliminated" count={rejected} type="error" />
              </tbody>
            </table>
          </div>
        </div>

        {/* System Performance Gauges */}
        <div className="glass-card">
          <h3 style={{ marginBottom: '24px', fontSize: '16px', fontWeight: 800 }}>PERFORMANCE SLA</h3>
          <div style={{ display: 'grid', gap: '24px' }}>
            <Gauge label="Active Candidates" value={active} percentage={totalCandidates > 0 ? Math.round((active / totalCandidates) * 100) : 0} color="#3b82f6" />
            <Gauge label="Overall Pass Rate" value={`${passRate}%`} percentage={passRate} color="#10b981" />
            <Gauge label="Elimination Rate" value={`${failRate}%`} percentage={failRate} color="#ef4444" />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
        {/* Hardware / Device Grid */}
        <div className="glass-card">
          <h3 style={{ marginBottom: '24px', fontSize: '16px', fontWeight: 800 }}>HARDWARE GRID</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {devices.map(device => (
              <div key={device.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 800 }}>{device.id}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{device.type}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: device.status === 'online' ? '#10b981' : '#ef4444' }}></div>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>{device.status}</div>
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{device.lastSync}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Incident Alert Feed */}
        <div className="glass-card">
          <h3 style={{ marginBottom: '24px', fontSize: '16px', fontWeight: 800 }}>INCIDENT FEED (REAL-TIME)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '250px', overflowY: 'auto', paddingRight: '8px' }}>
            {alerts.length === 0 ? (
               <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '14px' }}>No active incidents</div>
            ) : (
              alerts.map(alert => (
                <div key={alert.id} style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  borderLeft: `4px solid ${alert.type === 'error' ? '#ef4444' : alert.type === 'warning' ? '#f59e0b' : '#3b82f6'}`, 
                  padding: '12px',
                  background: 'rgba(0,0,0,0.02)',
                  borderRadius: '0 8px 8px 0'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{alert.message}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{alert.time} • SYSTEM LOG</div>
                  </div>
                  <Icon icon={alert.type === 'error' ? "ion:alert-circle" : "ion:information-circle"} color={alert.type === 'error' ? '#ef4444' : '#3b82f6'} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const OpRow = ({ name, count, type }) => {
  const isError = type === 'error';
  const isDone = type === 'completed';
  const isEmpty = count === 0;

  let statusType = isEmpty ? 'info' : isError ? 'error' : isDone ? 'success' : 'warning';
  let statusText = isEmpty ? 'Idle' : isError ? 'Eliminated' : isDone ? 'Completed' : 'Active';
  let barColor = isError ? '#ef4444' : isDone ? '#10b981' : count > 0 ? '#3b82f6' : '#e2e8f0';

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
      <td style={{ padding: '14px 12px', fontSize: '14px', fontWeight: 600 }}>{name}</td>
      <td style={{ padding: '14px 12px' }}>
        <Badge text={statusText} type={statusType} />
      </td>
      <td style={{ padding: '14px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '60px', height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(100, count * 5)}%`, height: '100%', background: barColor, borderRadius: '3px', transition: 'width 0.5s' }}></div>
          </div>
          <span style={{ fontSize: '13px', fontWeight: 800, color: isError ? '#ef4444' : isDone ? '#10b981' : 'var(--text-main)' }}>{count}</span>
        </div>
      </td>
    </tr>
  );
};

const Gauge = ({ label, value, percentage, color }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
      <span style={{ fontSize: '12px', fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: '12px', fontWeight: 800, color }}>{typeof value === 'number' ? value.toLocaleString() : value}</span>
    </div>
    <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
      <div style={{ width: `${percentage}%`, height: '100%', background: color, borderRadius: '4px', transition: 'width 0.5s' }}></div>
    </div>
  </div>
);

const MetricCard = ({ title, value, icon, color = 'var(--primary)', trend }) => (
  <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '24px', padding: '32px' }}>
    <div style={{ width: '56px', height: '56px', background: `${color}11`, color, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon icon={icon} width="28" height="28" style={{ margin: 'auto' }} />
    </div>
    <div>
      <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: '24px', fontWeight: 800 }}>{value}</div>
      {trend && <div style={{ fontSize: '11px', color: '#10b981', fontWeight: 700 }}>{trend} increase</div>}
    </div>
  </div>
);

const Badge = ({ text, type }) => {
  const colors = {
    success: { bg: '#dcfce7', text: '#166534' },
    warning: { bg: '#fef3c7', text: '#92400e' },
    error: { bg: '#fee2e2', text: '#991b1b' },
    info: { bg: '#e0f2fe', text: '#075985' }
  };
  return (
    <span style={{ 
      padding: '4px 8px', 
      borderRadius: '6px', 
      fontSize: '11px', 
      fontWeight: 700, 
      backgroundColor: colors[type].bg, 
      color: colors[type].text 
    }}>{text}</span>
  );
};

export default LiveCommandCenter;
